import { FC } from "react";
import { Box, Grid } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
	CalculationMethods,
	ChainTransportModelType,
	EnvironmentalKpi,
	TransportModelType,
	TypeTransportModelType,
} from "../../../../../common/modelTypes";
import { col4, col6 } from "../../../../../common/utils";
import PieChartDistributionType, { DataChart } from "../../../../../components/charts/graph/PieChartDistributionType";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import TableData from "../../../../../components/common/table/TableData";
import {
	DonutChartItemDTO,
	EmissionsTransportsResultsDatumDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { useChartColors } from "../../../../../theme/theme";
import EmissionChaiTransport from "../../components/EmissionChainTransport";
import { dataColTransport, getColumnsTransport, getRowsTransport } from "./data";

interface Props {
	keyEmissions: string;
	transportEmissions: TransportModelType;
	calculationMethods: CalculationMethods;
	nodesCertification: CalculationMethods;
	environmentalKpi: EnvironmentalKpi;
	typeTransport: TypeTransportModelType;
	chainsTransport: ChainTransportModelType;
}

type EmissionType = keyof Pick<EmissionsTransportsResultsDatumDTO, "co2Ttw" | "co2Wtw">;

const ThirdParty: FC<Props> = ({
	transportEmissions,
	keyEmissions,
	calculationMethods,
	nodesCertification,
	environmentalKpi,
	typeTransport,
	chainsTransport,
}) => {
	const { t } = useTranslation("result.transport");
	const { chartColors } = useChartColors();

	// Color palette matching data order
	// The order is IMPORTANT
	const colors = [
		chartColors.method1,
		chartColors.method2,
		chartColors.method3,
		chartColors.method4,
		chartColors.method5,
		chartColors.method6,
	];

	const getDataChart = (data: DonutChartItemDTO[], t: TFunction): DataChart[] => {
		return (
			data.map((value, index) => {
				return {
					id: index,
					percent: value.percentage ? Number(value.percentage.toFixed(2)) : 0,
					value: value.value ?? 0,
					label: value.label || "",
					icon: <Box borderRadius={0.8} width={12} height={12} sx={{ backgroundColor: colors[index] }} />,
				};
			}) || []
		);
	};

	const overrideFieldName = (old?: string) => {
		switch (old) {
			case "emissions":
				return keyEmissions;
			default:
				return old;
		}
	};

	const isWtw = keyEmissions === "co2Wtw";

	const marketBase: MarketBaseType = {
		emissions: isWtw
			? transportEmissions.data?.thirdParty?.current.marketBasedWtw
			: transportEmissions.data?.thirdParty?.current.marketBasedTtw,
		delta: isWtw
			? transportEmissions.data?.thirdParty?.current.marketBasedWtwDelta
			: transportEmissions.data?.thirdParty?.current.marketBasedTtwDelta,
		coverage: isWtw
			? transportEmissions.data?.thirdParty?.current.marketBasedWtwCoverage
			: transportEmissions.data?.thirdParty?.current.marketBasedTtwCoverage,
		isLoading: transportEmissions.isLoading,
		onReload: transportEmissions.reload,
	};

	return (
		<Grid container spacing={2}>
			<Grid size={col4} height={180}>
				<NumericValueWidget
					title={t("transport emissions")}
					delta={transportEmissions?.data?.thirdParty?.delta?.[keyEmissions as EmissionType]}
					value={transportEmissions.data?.thirdParty?.current?.[keyEmissions as EmissionType]?.value}
					measure={transportEmissions.data?.thirdParty?.current?.[keyEmissions as EmissionType]?.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col4} height={180}>
				<NumericValueWidget
					title={t("gross weight")}
					value={transportEmissions.data?.thirdParty.current?.weight.value}
					measure={transportEmissions.data?.thirdParty.current?.weight.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col4} height={180}>
				<NumericValueWidget
					title={t("tons km")}
					value={transportEmissions.data?.thirdParty.current?.tkm.value}
					measure={transportEmissions.data?.thirdParty.current?.tkm.measure}
					isLoading={transportEmissions.isLoading}
					onReload={transportEmissions.reload}
				/>
			</Grid>
			<Grid size={col6} minHeight={320}>
				<PieChartDistributionType
					totalEmissions={calculationMethods?.data?.total.value}
					keyEmissions={calculationMethods?.data?.total.measure}
					title={t("methodology used")}
					isLoading={calculationMethods?.isLoading || false}
					onReload={calculationMethods?.reload}
					dataChart={calculationMethods?.data?.items && getDataChart(calculationMethods?.data?.items, t)}
					colors={colors}
				/>
			</Grid>
			<Grid size={col6} minHeight={320}>
				<PieChartDistributionType
					totalEmissions={nodesCertification?.data?.total.value}
					keyEmissions={nodesCertification?.data?.total.measure}
					title={t("certification presence")}
					isLoading={nodesCertification?.isLoading || false}
					onReload={nodesCertification?.reload}
					dataChart={nodesCertification?.data?.items && getDataChart(nodesCertification?.data?.items, t)}
					colors={colors}
				/>
			</Grid>
			<Grid size={col6} minHeight={360}>
				<TableData
					columns={getColumnsTransport(
						dataColTransport,
						typeTransport.data ? typeTransport.data.total : {},
						t,
						overrideFieldName
					)}
					rows={typeTransport.data ? getRowsTransport(typeTransport.data, keyEmissions, t) : []}
					title={t("emissions by transport mode")}
					isLoading={typeTransport.isLoading}
					onReload={typeTransport.reload}
				/>
			</Grid>
			<Grid size={col6}>
				<EmissionChaiTransport
					title={t("transport chain results")}
					data={chainsTransport.data}
					isLoading={chainsTransport.isLoading}
					onReload={chainsTransport.reload}
				/>
			</Grid>

			<AmbientalKpiWidget
				bio={transportEmissions.data?.thirdParty.current.biogenic}
				pmx={transportEmissions.data?.thirdParty.current.pmx}
				pmxCoverage={transportEmissions.data?.thirdParty.current.pmxCoverage}
				bioCoverage={transportEmissions.data?.thirdParty.current.biogenicCoverage}
				loading={transportEmissions.isLoading}
				onReload={transportEmissions.reload}
			/>

			<MarketBaseWidget marketBase={marketBase} />
		</Grid>
	);
};

export default ThirdParty;
